import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import { AuthProvider } from "./auth/AuthProvider";
import RequireAuth from "./auth/requireAuth";
import AppLayout from "./components/layout/AppLayout";
import LoginPage from "./pages/login-page";
import ForgotPasswordPage from "./pages/forgot-password";
import ResetPasswordPage from "./pages/reset-password";
import SubcontractorManagement from './pages/directory';
import ProjectDetail from './pages/project-detail';
import UserProfile from './pages/user-profile';
import UserManagementPage from './pages/user-management';
import ProjectSchedulePage from './pages/project-schedule';
import BudgetPage from './pages/project-workspace/BudgetPage';
import ContractsPage from './pages/project-workspace/ContractsPage';
import ExtrasPage from './pages/project-workspace/ExtrasPage';
import SubInvoicesPage from './pages/project-workspace/SubInvoicesPage';
import ClientInvoicesPage from './pages/project-workspace/ClientInvoicesPage';
import QuittancesPage from './pages/project-workspace/QuittancesPage';
import ExpensesPage from './pages/project-workspace/ExpensesPage';
import SubcontractChangeOrdersPage from './pages/subcontract-change-orders-management-page';
import InvoiceInboxPage from './pages/invoice-inbox';
import TeamSchedulePage from './pages/team-schedule';
import ExpenseRedirectPage from './pages/invoice-inbox/ExpenseRedirectPage';
import DocumentsPage from './pages/project-workspace/DocumentsPage';
import SchedulePage from './pages/project-workspace/SchedulePage';
import RFIPage from './pages/project-workspace/RFIPage';
import DailyLogPage from './pages/project-workspace/DailyLogPage';
import ProjectDirectoryPage from './pages/project-workspace/ProjectDirectoryPage';
import OrgSettingsPage from './pages/org-settings';
import Project360Page from './pages/project360';
import BrainPage from './pages/brain';
import RequireBrain from './auth/RequireBrain';
import OnXPage from './pages/onx';
import FactureApprovalPage from './pages/facture-approval';
import SuperAdminPage from './pages/super-admin';
import { useAuth } from './auth/AuthProvider';
import useTranslation from './hooks/useTranslation';
import Icon from './components/AppIcon';
import {
  PagePermissionsProvider,
  usePagePermissions,
} from './utils/pagePermissions';
import { PAGE } from './constants/pageKeys';

/**
 * Ecran de dernier recours : le gabarit de l'utilisateur n'ouvre aucune page,
 * pas meme la liste des projets. Mieux vaut une phrase claire qu'une page
 * blanche ou une boucle de redirection.
 */
const NoAccessScreen = () => {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md w-full text-center">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <Icon name="Lock" size={22} className="text-amber-600" />
        </div>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">
          {t('common.accessDenied.title')}
        </h1>
        <p className="text-sm text-gray-600 leading-relaxed mb-6">
          {t('common.accessDenied.message')}
        </p>
        <button
          type="button"
          onClick={() => signOut?.()}
          className="px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {t('auth.signOut') || 'Sign out'}
        </button>
      </div>
    </div>
  );
};

/**
 * Garde de page par gabarit.
 *
 * Trois etats, dans cet ordre :
 *   1. reponse pas encore arrivee  -> on ne rend RIEN. Surtout pas une
 *      redirection : elle ejecterait un utilisateur legitime pendant le
 *      chargement.
 *   2. page autorisee              -> on rend.
 *   3. page refusee                -> retour a la liste des projets, avec le
 *      motif porte par l'etat de navigation ; si meme la liste est fermee,
 *      l'ecran ci-dessus. Jamais de boucle.
 */
const RequirePage = ({ pageKey, children }) => {
  const { ready, canAccessPage } = usePagePermissions();
  const location = useLocation();

  if (!ready) return null;
  if (canAccessPage(pageKey)) return children;
  if (canAccessPage(PAGE.APP_PROJECT_LIST)) {
    return (
      <Navigate
        to="/project-detail"
        replace
        state={{ deniedPage: pageKey, from: location?.pathname }}
      />
    );
  }
  return <NoAccessScreen />;
};

/** /super-admin ne passe PAS par un gabarit : c'est un drapeau de plateforme. */
const RequirePlatformAdmin = ({ children }) => {
  const { userProfile, profileLoading } = useAuth();
  if (profileLoading || !userProfile) return null;
  if (userProfile?.is_platform_admin === true) return children;
  return <Navigate to="/project-detail" replace />;
};

/**
 * Entree d'un projet. On ne peut plus envoyer tout le monde sur /budget : un
 * surintendant n'y a pas acces et se retrouverait dans un cul-de-sac. On ouvre
 * la PREMIERE page du menu que l'utilisateur peut reellement voir.
 */
const ProjectRedirect = () => {
  const { id } = useParams();
  const { ready, firstProjectPage } = usePagePermissions();
  if (!ready) return null;
  const segment = firstProjectPage();
  if (!segment) return <NoAccessScreen />;
  return <Navigate to={`/project/${id}/${segment}`} replace />;
};

const HomeRedirect = () => {
  const { userProfile } = useAuth();
  const { ready, canAccessPage } = usePagePermissions();
  if (!userProfile || !ready) return null;
  if (canAccessPage(PAGE.APP_PROJECT_LIST)) return <Navigate to="/project-detail" replace />;
  return <NoAccessScreen />;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PagePermissionsProvider>
        <ErrorBoundary>
          <ScrollToTop />
          <RouterRoutes>
            {/* Public route */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Protected routes with AppLayout wrapper */}
            <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/project-detail" element={<RequirePage pageKey={PAGE.APP_PROJECT_LIST}><ProjectDetail /></RequirePage>} />
              <Route path="/directory" element={<RequirePage pageKey={PAGE.APP_DIRECTORY}><SubcontractorManagement /></RequirePage>} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/users" element={<RequirePage pageKey={PAGE.APP_USERS}><UserManagementPage /></RequirePage>} />
              <Route path="/schedule" element={<RequirePage pageKey={PAGE.APP_SCHEDULE}><ProjectSchedulePage /></RequirePage>} />
              <Route path="/invoice-inbox" element={<RequirePage pageKey={PAGE.APP_INVOICE_INBOX}><InvoiceInboxPage /></RequirePage>} />
              <Route path="/team-schedule" element={<RequirePage pageKey={PAGE.APP_TEAM_SCHEDULE}><TeamSchedulePage /></RequirePage>} />
              <Route path="/invoice-inbox/expense-redirect" element={<RequirePage pageKey={PAGE.APP_INVOICE_INBOX}><ExpenseRedirectPage /></RequirePage>} />
              <Route path="/vue-projet" element={<RequirePage pageKey={PAGE.APP_PROJECT_VIEW}><Project360Page /></RequirePage>} />
              <Route path="/vue-projet/:projectId" element={<RequirePage pageKey={PAGE.APP_PROJECT_VIEW}><Project360Page /></RequirePage>} />
              {/* Brain — seules routes gardees par une permission. */}
              <Route path="/brain" element={<RequirePage pageKey={PAGE.APP_BRAIN}><RequireBrain><BrainPage /></RequireBrain></RequirePage>} />
              <Route path="/brain/:projectId" element={<RequirePage pageKey={PAGE.APP_BRAIN}><RequireBrain><BrainPage /></RequireBrain></RequirePage>} />
              {/* OnX — chat IA plein ecran */}
              <Route path="/onx" element={<RequirePage pageKey={PAGE.APP_ONX}><OnXPage /></RequirePage>} />
              
              {/* Project root redirect - clicking a project navigates here then redirects to budget */}
              <Route path="/project/:id" element={<ProjectRedirect />} />
              
              {/* Project Workspace Routes */}
              <Route path="/project/:id/budget" element={<RequirePage pageKey={PAGE.PROJECT_BUDGET}><BudgetPage /></RequirePage>} />
              <Route path="/project/:id/contracts" element={<RequirePage pageKey={PAGE.PROJECT_CONTRACTS}><ContractsPage /></RequirePage>} />
              <Route path="/project/:id/extras" element={<RequirePage pageKey={PAGE.PROJECT_EXTRAS}><ExtrasPage /></RequirePage>} />
              <Route path="/project/:id/expenses" element={<RequirePage pageKey={PAGE.PROJECT_EXPENSES}><ExpensesPage /></RequirePage>} />
              <Route path="/project/:id/sub-invoices" element={<RequirePage pageKey={PAGE.PROJECT_SUB_INVOICES}><SubInvoicesPage /></RequirePage>} />
              <Route path="/project/:id/sub-invoices/approve/:invoiceId" element={<RequirePage pageKey={PAGE.PROJECT_SUB_INVOICE_APPROVE}><FactureApprovalPage /></RequirePage>} />
              <Route path="/project/:id/client-invoices" element={<RequirePage pageKey={PAGE.PROJECT_CLIENT_INVOICES}><ClientInvoicesPage /></RequirePage>} />
              <Route path="/project/:id/quittances" element={<RequirePage pageKey={PAGE.PROJECT_QUITTANCES}><QuittancesPage /></RequirePage>} />
              <Route path="/project/:id/change-orders" element={<RequirePage pageKey={PAGE.PROJECT_CHANGE_ORDERS}><SubcontractChangeOrdersPage /></RequirePage>} />
              <Route path="/project/:id/documents" element={<RequirePage pageKey={PAGE.PROJECT_DOCUMENTS}><DocumentsPage /></RequirePage>} />
              <Route path="/project/:id/schedule" element={<RequirePage pageKey={PAGE.PROJECT_SCHEDULE}><SchedulePage /></RequirePage>} />
              <Route path="/project/:id/rfi" element={<RequirePage pageKey={PAGE.PROJECT_RFI}><RFIPage /></RequirePage>} />
              <Route path="/project/:id/daily-log" element={<RequirePage pageKey={PAGE.PROJECT_DAILY_LOG}><DailyLogPage /></RequirePage>} />
              <Route path="/project/:id/directory" element={<RequirePage pageKey={PAGE.PROJECT_DIRECTORY}><ProjectDirectoryPage /></RequirePage>} />
              <Route path="/org-settings" element={<RequirePage pageKey={PAGE.APP_ORG_SETTINGS}><OrgSettingsPage /></RequirePage>} />
              <Route path="/super-admin" element={<RequirePlatformAdmin><SuperAdminPage /></RequirePlatformAdmin>} />
            </Route>
            
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </ErrorBoundary>
        </PagePermissionsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
